import jsonExport from "jsonexport/dist";
import { useState } from "react";
import { downloadCSV, useGetIdentity, type Exporter } from "ra-core";
import { Link } from "react-router";
import { Columns3, ListChecks, Table as TableIcon } from "lucide-react";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { SortButton } from "@/components/admin/sort-button";
import { TextInput } from "@/components/admin/text-input";
import { DateField } from "@/components/admin/date-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { TopToolbar } from "../layout/TopToolbar";
import { tierColor, tierLetter } from "../dealsmap/leadUtils";
import type { Prospect, Sale } from "../types";
import { listingStatuses, prospectStages, primaryPhone } from "./prospectModel";
import { ProspectBulkAddToList } from "./ProspectBulkAddToList";
import { ProspectBulkDrivingRoute } from "./ProspectBulkDrivingRoute";
import { ProspectBulkSkipTrace } from "./ProspectBulkSkipTrace";
import { ProspectEmpty } from "./ProspectEmpty";
import { ProspectImportButton } from "./ProspectImportButton";
import { ProspectPipeline } from "./ProspectPipeline";

type ProspectView = "table" | "board";

const prospectFilters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput source="stage" choices={prospectStages} />,
  <SelectInput source="listing_status" choices={listingStatuses} />,
  <TextInput source="property_zip" label="Zip" />,
  <ReferenceInput source="group_id" reference="groups" label="List">
    <SelectInput optionText="name" label="List" />
  </ReferenceInput>,
];

const ViewToggle = ({
  view,
  setView,
}: {
  view: ProspectView;
  setView: (v: ProspectView) => void;
}) => (
  <div className="flex">
    <Button
      type="button"
      variant={view === "table" ? "secondary" : "ghost"}
      size="sm"
      aria-label="Table view"
      onClick={() => setView("table")}
    >
      <TableIcon className="size-4" />
    </Button>
    <Button
      type="button"
      variant={view === "board" ? "secondary" : "ghost"}
      size="sm"
      aria-label="Board view"
      onClick={() => setView("board")}
    >
      <Columns3 className="size-4" />
    </Button>
  </div>
);

const ProspectListActions = ({
  view,
  setView,
}: {
  view: ProspectView;
  setView: (v: ProspectView) => void;
}) => (
  <TopToolbar>
    <ViewToggle view={view} setView={setView} />
    {view === "table" ? (
      <SortButton
        fields={["owner_name", "auction_date", "stage", "property_zip"]}
      />
    ) : null}
    <FilterButton />
    <Button asChild variant="outline" size="sm" className="h-9">
      <Link to="/groups" title="View, edit, and remove your lists">
        <ListChecks className="size-4" /> Lists
      </Link>
    </Button>
    <ProspectImportButton />
    <ExportButton exporter={exporter} />
    <CreateButton />
  </TopToolbar>
);

export const ProspectList = () => {
  const { identity } = useGetIdentity();
  const [view, setView] = useState<ProspectView>("table");
  if (!identity) return null;

  return (
    <List
      title={false}
      actions={<ProspectListActions view={view} setView={setView} />}
      filters={prospectFilters}
      perPage={view === "board" ? 100 : 25}
      sort={{ field: "auction_date", order: "ASC" }}
      exporter={exporter}
      empty={<ProspectEmpty />}
    >
      {view === "board" ? (
        <ProspectPipeline />
      ) : (
        <DataTable
          rowClassName={() => "cursor-pointer"}
          bulkActionButtons={
            <>
              <ProspectBulkAddToList />
              <ProspectBulkDrivingRoute />
              <ProspectBulkSkipTrace />
            </>
          }
        >
        <DataTable.Col source="owner_name" label="Owner" />
        <DataTable.Col source="property_address" label="Property" />
        <DataTable.Col
          label="City / State / Zip"
          render={(record: Prospect) =>
            [record.property_city, record.property_state, record.property_zip]
              .filter(Boolean)
              .join(", ")
          }
        />
        <DataTable.Col
          label="Phone"
          render={(record: Prospect) => primaryPhone(record) ?? ""}
        />
        <DataTable.Col
          source="stage"
          label="Stage"
          render={(record: Prospect) =>
            record.stage ? (
              <Badge variant="secondary">{record.stage}</Badge>
            ) : null
          }
        />
        <DataTable.Col
          source="deal_tier"
          label="Tier"
          render={(record: Prospect) => {
            const letter = tierLetter(record.deal_tier);
            return letter ? (
              <Badge
                className="text-white border-transparent"
                style={{ backgroundColor: tierColor(record.deal_tier) }}
              >
                {letter}
              </Badge>
            ) : null;
          }}
        />
        <DataTable.Col
          source="listing_status"
          label="Listing"
          render={(record: Prospect) => record.listing_status ?? ""}
        />
        <DataTable.Col source="auction_date" label="Auction">
          <DateField source="auction_date" />
        </DataTable.Col>
        </DataTable>
      )}
    </List>
  );
};

const exporter: Exporter<Prospect> = async (records, fetchRelatedRecords) => {
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");

  const prospects = records.map((prospect) => ({
    ...prospect,
    owner:
      prospect.sales_id != null
        ? `${sales[prospect.sales_id].first_name} ${sales[prospect.sales_id].last_name}`
        : undefined,
    phones: JSON.stringify(prospect.phones),
    emails: JSON.stringify(prospect.emails),
  }));

  return jsonExport(prospects, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "prospects");
  });
};
